import React from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import SearchPage from './pages/SearchPage';
import WorkOrderPage from './pages/WorkOrderPage';
import PrintPage from './pages/PrintPage';
import DashboardPage from './pages/DashboardPage';
import RmDashboardPage from './pages/RmDashboardPage';
import MatrixPage from './pages/MatrixPage';
import PmReviewPage from './pages/PmReviewPage';
import ExceptionsPage from './pages/ExceptionsPage';
import BulkPage from './pages/BulkPage';
import BulkHistoryPage from './pages/BulkHistoryPage';
import './App.css';

const navStyle = { display: 'flex', gap: 24, padding: '12px 24px', background: '#1a1a2e', color: '#fff', alignItems: 'center' };
const linkStyle = { color: '#ccc', textDecoration: 'none', fontSize: 14 };
const activeLinkStyle = { ...linkStyle, color: '#fff', fontWeight: 600, borderBottom: '2px solid #4a9eff' };

function Nav() {
    const loc = useLocation();
    const { instance, accounts } = useMsal();
    const account = accounts[0];

    // Which pages this host can actually serve. Bulk operations refuse to run
    // without a writable audit trail, and the PM reporting views need the MC
    // database — so where those are absent the tab is hidden rather than left
    // to fail when clicked.
    const [caps, setCaps] = React.useState(null);
    React.useEffect(() => {
        fetch('/api/v1/health?deep=1', { headers: { Accept: 'application/json' } })
            .then(r => r.json()).then(setCaps).catch(() => setCaps({}));
    }, []);

    const canAudit = caps?.sql?.app?.ok !== false;
    const canReport = caps?.sql?.mc?.ok !== false;
    // The R&M dashboard reads the mc_ replica, which is reachable from the App
    // Service where MC prod is not — so it has its own capability, not canReport.
    const canRm = caps?.sql?.dw?.ok !== false;

    return (
        <nav style={navStyle}>
            <span style={{ fontSize: 18, fontWeight: 700, marginRight: 32, color: '#4a9eff' }}>MC Work Orders</span>
            {[
                { to: '/', label: 'Search', show: true },
                { to: '/rm', label: 'R&M dashboard', show: canRm },
                { to: '/dashboard', label: 'PM compliance', show: canReport },
                { to: '/review', label: 'Programme review', show: true },
                { to: '/matrix', label: 'Site matrix', show: canReport },
                { to: '/exceptions', label: 'Exceptions', show: canReport },
                { to: '/bulk', label: 'Bulk issue', show: canAudit },
            ].filter(l => l.show).map(l => (
                <Link key={l.to} to={l.to} style={loc.pathname === l.to ? activeLinkStyle : linkStyle}>{l.label}</Link>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                {account && <span style={{ fontSize: 12, color: '#aaa' }}>{account.name || account.username}</span>}
                {account && (
                    <button onClick={() => instance.logoutRedirect()}
                        style={{ background: 'none', border: '1px solid #4a9eff', color: '#4a9eff', padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                        Logout
                    </button>
                )}
            </div>
        </nav>
    );
}

function AuthenticatedApp() {
    return (
        <BrowserRouter>
            <Nav />
            <div style={{ maxWidth: 1400, margin: '24px auto', padding: '0 24px' }}>
                <Routes>
                    <Route path="/" element={<SearchPage />} />
                    <Route path="/wo/:pk" element={<WorkOrderPage />} />
                    <Route path="/wo/:pk/print" element={<PrintPage />} />
                    <Route path="/rm" element={<RmDashboardPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/review" element={<PmReviewPage />} />
                    <Route path="/matrix" element={<MatrixPage />} />
                    <Route path="/exceptions" element={<ExceptionsPage />} />
                    <Route path="/bulk" element={<BulkPage />} />
                    <Route path="/bulk/history" element={<BulkHistoryPage />} />
                </Routes>
            </div>
        </BrowserRouter>
    );
}

function LoginPage() {
    const { instance } = useMsal();
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
            <h1 style={{ color: '#1a1a2e', marginBottom: 8 }}>MC Work Orders</h1>
            <p style={{ color: '#666', marginBottom: 24 }}>United Petroleum -- Maintenance Connection</p>
            <button onClick={() => instance.loginRedirect(loginRequest)}
                style={{ padding: '12px 32px', background: '#4a9eff', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>
                Sign In
            </button>
        </div>
    );
}

export default function App() {
    const isAuthenticated = useIsAuthenticated();
    return isAuthenticated ? <AuthenticatedApp /> : <LoginPage />;
}
